import React from 'react';

type CardVariant = 'default' | 'elevated' | 'outlined' | 'filled';

interface CardProps {
  variant?: CardVariant;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  className?: string;
  children: React.ReactNode;
  onClick?: () => void;
  hover?: boolean;
}

/**
 * Card Component - Clinic Studios Design System
 *
 * Variants:
 * - default: White background with subtle shadow
 * - elevated: White background with more prominent shadow
 * - outlined: White background with border, no shadow
 * - filled: Cream/primary tint background for emphasis
 */
const Card: React.FC<CardProps> = ({
  variant = 'default',
  padding = 'md',
  className = '',
  children,
  onClick,
  hover = false,
}) => {
  const baseStyles = 'rounded-card transition-all duration-150';

  const variants: Record<CardVariant, string> = {
    default: 'bg-white shadow-clinic-sm',
    elevated: 'bg-white shadow-clinic-md',
    outlined: 'bg-white border border-clinic-primary-light',
    filled: 'bg-clinic-cream-light border border-clinic-primary-lighter',
  };

  const paddings: Record<string, string> = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  };

  const hoverStyles = hover || onClick
    ? 'cursor-pointer hover:shadow-clinic-md hover:-translate-y-0.5'
    : '';

  const interactiveStyles = onClick ? 'focus:outline-none focus:ring-2 focus:ring-clinic-primary focus:ring-offset-2' : '';

  const Component = onClick ? 'button' : 'div';

  return (
    <Component
      className={`
        ${baseStyles}
        ${variants[variant]}
        ${paddings[padding]}
        ${hoverStyles}
        ${interactiveStyles}
        ${className}
      `.replace(/\s+/g, ' ').trim()}
      onClick={onClick}
      {...(onClick && { type: 'button' })}
    >
      {children}
    </Component>
  );
};

// Card Header subcomponent
interface CardHeaderProps {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  className?: string;
}

export const CardHeader: React.FC<CardHeaderProps> = ({
  title,
  subtitle,
  action,
  className = '',
}) => (
  <div className={`flex items-start justify-between mb-4 ${className}`}>
    <div>
      <h3 className="text-lg font-semibold text-clinic-dark font-body">{title}</h3>
      {subtitle && (
        <p className="text-sm text-clinic-muted mt-0.5">{subtitle}</p>
      )}
    </div>
    {action && <div className="flex-shrink-0 ml-4">{action}</div>}
  </div>
);

// Card Content subcomponent
interface CardContentProps {
  children: React.ReactNode;
  className?: string;
}

export const CardContent: React.FC<CardContentProps> = ({
  children,
  className = '',
}) => (
  <div className={className}>{children}</div>
);

// Card Footer subcomponent
interface CardFooterProps {
  children: React.ReactNode;
  className?: string;
  align?: 'left' | 'center' | 'right' | 'between';
}

export const CardFooter: React.FC<CardFooterProps> = ({
  children,
  className = '',
  align = 'right',
}) => {
  const alignments: Record<string, string> = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
    between: 'justify-between',
  };

  return (
    <div
      className={`
        flex items-center gap-3 mt-6 pt-4 border-t border-clinic-primary-lighter
        ${alignments[align]}
        ${className}
      `.replace(/\s+/g, ' ').trim()}
    >
      {children}
    </div>
  );
};

export default Card;
